import { useState } from 'react';
import { Alert, Button, Modal, Select, Skeleton, Switch, Table, Tag, message } from 'antd';
import { CheckOutlined, EditOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  confirmTeamContacts,
  getTeamContactsDetail,
  getTeamContactsMailing,
  updateTeamContactsMailing,
} from '@/services/teamAssignments.service';
import ContactsDialog from './ContactsDialog';
import { ContactHistory, ReviewTag, SectionTags } from './contactReviewParts';

const errorText = (e) => e?.response?.data?.message || e.message;

/** Section by section: which list the company's mail goes to and why. */
function MailingOverview({ companyId, detail, canManage, onChanged }) {
  const { t } = useTranslation('teamContacts');
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['team-contacts-mailing', companyId],
    queryFn: () => getTeamContactsMailing(companyId),
    retry: false,
  });
  const update = useMutation({
    mutationFn: (payload) => updateTeamContactsMailing(companyId, payload),
    onSuccess: () => {
      message.success(t('review.saved'));
      queryClient.invalidateQueries({ queryKey: ['team-contacts-mailing', companyId] });
      onChanged?.();
    },
    onError: (e) => message.error(errorText(e)),
  });
  if (isLoading) return <Skeleton active paragraph={{ rows: 3 }} />;
  if (!data) return null;
  return (
    <div className="grid gap-2">
      {!data.switchedOn && <Alert type="info" showIcon message={t('source.globalOff')} />}
      <Table
        size="small"
        rowKey="section"
        pagination={false}
        tableLayout="fixed"
        dataSource={data.sections.filter((s) => s.section !== 'INVOICES')}
        columns={[
          { title: t('source.section'), dataIndex: 'section', width: '18%', render: (s) => t(`sections.${s}`) },
          {
            title: t('source.source'),
            dataIndex: 'source',
            width: '34%',
            render: (source, row) => (
              <div className="whitespace-normal">
                <Tag color={source === 'CONTACTS' ? 'success' : 'default'} className="m-0">
                  {t(`source.${source}`)}
                </Tag>
                {row.reason && <div className="mt-1 text-xs opacity-70">{t(`source.reason.${row.reason}`)}</div>}
                {row.holder && row.relation && row.relation !== 'OWN' && (
                  <div className="mt-1 text-xs opacity-70">{t(`source.relation.${row.relation}`, { name: row.holder.name })}</div>
                )}
              </div>
            ),
          },
          {
            title: t('source.recipients'),
            key: 'list',
            render: (_, row) => (
              <div className="whitespace-normal text-xs [overflow-wrap:anywhere]">
                {[...row.to, ...row.cc].join(', ') || '—'}
                {row.dropped?.length > 0 && (
                  <div className="mt-1 text-[#d46b08]">{t('source.dropped', { list: row.dropped.join(', ') })}</div>
                )}
              </div>
            ),
          },
        ]}
      />
      {canManage && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              size="small"
              checked={!detail.review.mailingOff}
              loading={update.isPending}
              onChange={(on) => update.mutate({ mailingOff: !on })}
            />
            {t('source.companySwitch')}
          </label>
          {detail.related?.length > 0 && (
            <label className="flex items-center gap-2 text-sm">
              {t('source.holderLabel')}
              <Select
                allowClear
                size="small"
                style={{ width: 280 }}
                placeholder={t('source.holderAuto')}
                value={detail.review.holder || undefined}
                options={detail.related.map((c) => ({ value: c._id, label: c.name }))}
                onChange={(holder) => update.mutate({ holder: holder || null })}
              />
            </label>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * One company's contacts for the team: read them, change them, confirm them, see
 * what the client changed and which list the company's mail goes to. Works for an
 * HLB person with `employee` as their only role too: what they may do is the
 * server's answer.
 *
 * @param {Object} props
 * @param {string|null} props.companyId - The company to show; none keeps it closed
 * @param {Function} props.onClose
 * @param {Function} [props.onChanged] - Something changed, lists should reload
 */
export default function ContactReviewDialog({ companyId, onClose, onChanged }) {
  const { t } = useTranslation(['teamContacts', 'teamAssignments']);
  const [editing, setEditing] = useState(false);
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['team-contacts-detail', companyId],
    queryFn: () => getTeamContactsDetail(companyId),
    enabled: !!companyId,
    retry: false,
  });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['team-contacts-detail', companyId] });
    queryClient.invalidateQueries({ queryKey: ['team-contacts-mailing', companyId] });
    queryClient.invalidateQueries({ queryKey: ['team-contacts-summary'] });
    onChanged?.();
  };
  const confirm = useMutation({
    mutationFn: () => confirmTeamContacts(companyId, data.review.revision),
    onSuccess: () => {
      message.success(t('review.confirmedMessage'));
      refresh();
    },
    onError: (e) => {
      message.error(errorText(e));
      refresh();
    },
  });

  const review = data?.review;
  const reachable = (data?.contacts || []).some((c) => c.email && !c.inactive);

  return (
    <Modal
      open={!!companyId}
      width={980}
      title={`${t('review.title')}${data ? `: ${data.company.name}` : ''}`}
      onCancel={onClose}
      destroyOnClose
      footer={
        data?.canEdit ? (
          <div className="flex justify-end gap-2">
            <Button shape="round" icon={<EditOutlined />} onClick={() => setEditing(true)}>
              {t('contacts.edit', { ns: 'teamAssignments' })}
            </Button>
            <Button
              type="primary"
              shape="round"
              icon={<CheckOutlined />}
              disabled={!reachable || review?.status === 'CONFIRMED'}
              loading={confirm.isPending}
              onClick={() => confirm.mutate()}
            >
              {t('review.confirm')}
            </Button>
          </div>
        ) : null
      }
    >
      {isLoading && <Skeleton active />}
      {error && <Alert type="error" showIcon message={errorText(error)} />}
      {data && (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <ReviewTag label={t('review.hlb')} status={review.status} by={review.confirmedBy} at={review.confirmedAt} />
            <ReviewTag label={t('review.client')} status={review.clientStatus} by={review.clientConfirmedBy} at={review.clientConfirmedAt} />
          </div>
          {review.clientChanged && (
            <Alert type="warning" showIcon message={t('review.clientChanged', { name: review.lastChangedBy })} />
          )}
          {!data.canEdit && <Alert type="info" showIcon message={t('review.readOnly')} />}
          <Table
            size="small"
            rowKey="_id"
            pagination={false}
            tableLayout="fixed"
            dataSource={data.contacts}
            locale={{ emptyText: t('contacts.none', { ns: 'teamAssignments' }) }}
            rowClassName={(r) => (r.inactive ? 'opacity-50 line-through' : '')}
            columns={[
              {
                title: t('contacts.name', { ns: 'teamAssignments' }),
                dataIndex: 'name',
                width: '26%',
                render: (v, r) => (
                  <div className="whitespace-normal">
                    {v || '—'}
                    {r.position ? <span className="opacity-60"> · {r.position}</span> : null}
                    {r.note ? <div className="text-xs opacity-60">{r.note}</div> : null}
                  </div>
                ),
              },
              {
                title: t('contacts.email', { ns: 'teamAssignments' }),
                dataIndex: 'email',
                width: '32%',
                render: (v) => <div className="whitespace-normal [overflow-wrap:anywhere]">{v || '—'}</div>,
              },
              {
                title: t('contacts.sections', { ns: 'teamAssignments' }),
                dataIndex: 'sections',
                render: (s) => (
                  <div className="whitespace-normal">
                    <SectionTags sections={s} />
                  </div>
                ),
              },
            ]}
          />
          <div>
            <h4 className="mb-2 font-semibold">{t('source.title')}</h4>
            <MailingOverview companyId={companyId} detail={data} canManage={data.canManage} onChanged={refresh} />
          </div>
          <div>
            <h4 className="mb-2 font-semibold">{t('history.title')}</h4>
            <ContactHistory entries={data.history} />
          </div>
        </div>
      )}
      {editing && data && (
        <ContactsDialog
          open
          company={{ _id: data.company._id, name: data.company.name, clientContacts: data.contacts, contactsReview: review }}
          onClose={() => setEditing(false)}
          onDone={refresh}
        />
      )}
    </Modal>
  );
}
